import { useEffect, useMemo, useRef, useState } from "react";
import { Search, AlignLeft, ListOrdered, Sparkles, AudioLines } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtTime } from "@/lib/api";
import { LANGS, useApp } from "@/lib/i18n";
import { SpeakerDialog } from "./Transcript";
import { ParagraphView, SectionNav, SegmentView } from "./TranscriptViews";
import { CopyButton, ExportDialog } from "./TranscriptExport";
import { TaskButtons, useTranscriptTasks } from "./TranscriptTasks";
import { SpeakerSuggestions } from "./SpeakerSuggestions";
import { useAuth } from "@/context/AuthContext";
import { isPaid } from "@/config/plans";
import { segText, wordCount } from "./transcriptUtils";

const Stat = ({ label, value, id }) => (
  <div className="rounded-xl bg-slate-50 px-3 py-2"><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p><p className="font-heading text-lg font-extrabold" data-testid={`transcript-stat-${id}`}>{value}</p></div>
);

const Toggle = ({ checked, onChange, label, id }) => (
  <label className="flex items-center gap-2 text-xs font-medium text-slate-600"><Switch checked={checked} onCheckedChange={onChange} data-testid={id} /> {label}</label>
);

function useFollow(segments, current, follow) {
  const box = useRef(null);
  const last = useRef(null);
  useEffect(() => {
    const s = segments.find((x) => current >= x.start && current < x.end);
    if (!follow || !s || s.id === last.current || !box.current) return;
    last.current = s.id;
    const el = box.current.querySelector(`[data-seg="${s.id}"]`);
    if (!el) return;
    const top = el.getBoundingClientRect().top - box.current.getBoundingClientRect().top + box.current.scrollTop - box.current.clientHeight / 3;
    box.current.scrollTo({ top, behavior: "smooth" });
  }, [segments, current, follow]);
  return box;
}

export const TranscriptTab = ({ meeting, segments, onSeek, current: rawCurrent, canWrite, onNames, onSegments, onReload, onMeeting }) => {
  const { L } = useApp();
  const paid = isPaid(useAuth().workspace);
  const current = rawCurrent > 0 ? rawCurrent : -1;
  const [q, setQ] = useState("");
  const [layout, setLayout] = useState("paragraph");
  const [version, setVersion] = useState("clean");
  const [showTs, setShowTs] = useState(true);
  const [follow, setFollow] = useState(true);
  const box = useFollow(segments, current, follow);
  const sections = meeting.transcript_sections || [];
  const names = meeting.speaker_names || {};
  const { running, start } = useTranscriptTasks(meeting.id, (t) => {
    toast.success({ polish: L("Transkrip telah dipulihkan", "Transcript polished"), diarize: L("Pembicara dikesan semula melalui suara", "Speakers re-detected by voice") }[t.kind] || L("Terjemahan siap", "Translation ready"));
    if (t.kind === "translate") setVersion(t.lang);
    onReload();
  });
  const versions = useMemo(() => [
    ...(meeting.transcript_polished ? [["clean", L("Dipulihkan AI", "AI-polished")], ["raw", L("Teks asal (verbatim)", "Original (verbatim)")]] : [["clean", L("Teks asal", "Original")]]),
    ...(meeting.transcript_translations || []).map((k) => { const l = LANGS.find((x) => x[0] === k); return [k, `${L("Terjemahan", "Translation")}: ${l ? L(l[1], l[2]) : k}`]; }),
  ], [meeting.transcript_polished, meeting.transcript_translations, L]);
  const hits = q ? segments.filter((s) => segText(s, version).toLowerCase().includes(q.toLowerCase())).length : 0;
  const updateSeg = (ns) => onSegments(segments.map((s) => (s.id === ns.id ? { ...ns, edited: true, translations: undefined } : s)));
  const viewProps = { segs: segments, sections, names, version, current, q, onSeek, showTs };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="transcript-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label={L("Tempoh", "Duration")} value={fmtTime(meeting.duration)} id="duration" />
          <Stat label={L("Perkataan", "Words")} value={wordCount(segments).toLocaleString()} id="words" />
          <Stat label={L("Pembicara", "Speakers")} value={Object.keys(names).length} id="speakers" />
          <Stat label={L("Topik", "Topics")} value={sections.length} id="topics" />
        </div>
        <div className="flex flex-col items-end gap-1.5">
        <span className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${meeting.diarization === "voice" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="diarization-badge"><AudioLines size={12} /> {meeting.diarization === "voice" ? L("Pembicara dikesan melalui suara", "Speakers detected by voice") : L("Pembicara dianggar AI", "Speakers estimated by AI")}</span>
        {meeting.transcript_polished && <span className="flex items-center gap-1 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700" data-testid="polished-badge"><Sparkles size={12} /> {L("Dipulihkan AI", "AI-polished")}</span>}
        </div>
      </div>
      <SpeakerSuggestions meeting={meeting} canWrite={canWrite} onMeeting={onMeeting} />
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        {canWrite && paid && <TaskButtons meeting={meeting} running={running} start={start} />}
        {canWrite && <SpeakerDialog meetingId={meeting.id} names={names} onSaved={onNames} voice={meeting.diarization === "voice"} />}
        <div className="ml-auto flex flex-wrap gap-2">
          <CopyButton segs={segments} sections={sections} names={names} opts={{ version, timestamps: showTs, speakers: true }} />
          {paid && <ExportDialog meeting={meeting} versions={versions} defaultVersion={version} />}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Cari dalam transkrip…", "Search transcript…")} className="h-9 rounded-xl pl-9" data-testid="transcript-search-input" aria-label="Cari transkrip" />
        </div>
        <Select value={version} onValueChange={setVersion}>
          <SelectTrigger className="h-9 w-auto min-w-[170px] rounded-xl" data-testid="transcript-version-select"><SelectValue /></SelectTrigger>
          <SelectContent>{versions.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`version-option-${v}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex rounded-xl border border-slate-200 p-0.5">
          {[["paragraph", AlignLeft, L("Perenggan", "Paragraphs")], ["segment", ListOrdered, L("Segmen", "Segments")]].map(([k, Icon, label]) => (
            <button key={k} onClick={() => setLayout(k)} className={`flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${layout === k ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`layout-${k}-btn`}><Icon size={13} /> {label}</button>
          ))}
        </div>
        <Toggle checked={showTs} onChange={setShowTs} label={L("Cap masa", "Timestamps")} id="toggle-timestamps" />
        <Toggle checked={follow} onChange={setFollow} label={L("Ikut audio", "Follow audio")} id="toggle-follow" />
      </div>
      <p className="mt-3 text-xs text-slate-500" data-testid="transcript-count">{q ? `${hits} ${L("padanan", "matches")} · ` : ""}{segments.length} {L("segmen", "segments")}{layout === "segment" && canWrite && version === "clean" ? ` · ${L("hover segmen untuk edit", "hover a segment to edit")}` : ""}</p>
      <div className={`mt-3 grid gap-5 ${sections.length ? "lg:grid-cols-[220px_1fr]" : ""}`}>
        {sections.length > 0 && <aside className="hidden lg:block"><p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{L("Topik", "Topics")}</p><SectionNav sections={sections} onSeek={onSeek} /></aside>}
        <div ref={box} className="slim-scroll max-h-[640px] overflow-y-auto pr-2" data-testid="transcript-list">
          {layout === "paragraph" ? <ParagraphView {...viewProps} /> : <SegmentView {...viewProps} meetingId={meeting.id} canEdit={canWrite} onSaved={updateSeg} />}
        </div>
      </div>
    </div>
  );
};
